import { emitCustomEvent } from '@/composables/useEventBasedDialog';
import { YardTaskCheckEntity } from '@nova/core';

import type { CheckTaskViewData } from '@nova/core';

type ValidatedContext = {
  task: CheckTaskViewData;
  taskId: string;
  checkId: string;
  relatedObject: NonNullable<CheckTaskViewData['relatedObject']>;
};

type EntityActionHandler = (ctx: ValidatedContext) => void;

/**
 * Maps each supported check-task entity type to its primary-action handler.
 * To add support for a new entity, add an entry here — no changes needed elsewhere.
 */
const ENTITY_ACTION_HANDLERS: Partial<Record<YardTaskCheckEntity, EntityActionHandler>> = {
  [YardTaskCheckEntity.Spot]: ({ taskId, checkId, relatedObject }) => {
    emitCustomEvent('open-check-spot-panel', {
      taskId,
      checkId,
      spotId: relatedObject.id,
      spotEvents: relatedObject.spotEvents ?? []
    });
  },

  [YardTaskCheckEntity.AssetContainer]: ({ task, taskId, checkId, relatedObject }) => {
    const wid = task.warehouseId;
    if (!wid) {
      return;
    }
    emitCustomEvent('open-asset-check-wizard', {
      taskId,
      checkId,
      assetContainerId: relatedObject.id,
      warehouseId: wid,
      taskStatus: task.status,
      checkAssetContainers: task.checkAssetContainers
    });
  }
};

export function useCheckTaskPrimaryAction() {
  function triggerPrimaryAction(task: CheckTaskViewData): void {
    const taskId = task.id;
    const checkId = task.checkId;
    const relatedObject = task.relatedObject;
    const handler =
      task.relatedEntity != null ? ENTITY_ACTION_HANDLERS[task.relatedEntity] : undefined;

    if (!taskId || !checkId || !relatedObject?.id || !handler) {
      return;
    }

    handler({ task, taskId, checkId, relatedObject });
  }

  return { triggerPrimaryAction };
}
